import type { WidgetInitialConfig, WidgetTabId } from '../widgetConfig';

export const WIDGET_ANALYTICS_SCHEMA_VERSION = 1;
export const WIDGET_ANALYTICS_DOM_EVENT_NAME = 'passenger-widget:analytics';

export type WidgetAnalyticsActivity = WidgetTabId;
export type WidgetAnalyticsPage = 'search' | 'results' | 'details';
export type WidgetAnalyticsAsyncStatus = 'success' | 'empty' | 'error';
export type WidgetAnalyticsLocationType = 'place' | 'stop' | 'unknown';

export interface WidgetBuildInfo {
  timestamp: string;
  version: string;
  commitSha: string;
}

export interface WidgetAnalyticsRuntimeMetadata {
  tenant: string;
  initialConfig: WidgetInitialConfig;
  buildInfo: WidgetBuildInfo;
}

// TODO: this may need to be stripped back quite a lot
export interface WidgetAnalyticsPayloadMap {
  widget_loaded: {
    initialTab: WidgetTabId;
    hasInitialOrigin: boolean;
    hasInitialDestination: boolean;
    hasDepartureStop: boolean;
  };
  activity_changed: {
    activity: WidgetAnalyticsActivity;
  };
  plan_search_submitted: {
    originQuery: string;
    destinationQuery: string;
    originType: WidgetAnalyticsLocationType;
    destinationType: WidgetAnalyticsLocationType;
    timeMode: 'now' | 'leaveAt' | 'arriveBy';
    travelModes: string[];
    walkingSpeed: 'slow' | 'medium' | 'fast';
    minimizeWalking: boolean;
  };
  plan_pagination_requested: {
    direction: 'earlier' | 'later';
  };
  plan_results_loaded: {
    status: WidgetAnalyticsAsyncStatus;
    planCount: number;
    operatorCodes: string[];
    operatorNames: string[];
    lineIds: string[];
    modes: string[];
    errorMessage?: string;
  };
  plan_result_selected: {
    planIndex: number;
    legCount: number;
    operatorCodes: string[];
    operatorNames: string[];
    lineIds: string[];
    modes: string[];
  };
  plan_details_tab_changed: {
    tabId: 'itinerary' | 'map';
    planIndex: number;
  };
  departures_search_submitted: {
    locationQuery: string;
    selectedLocationType: WidgetAnalyticsLocationType;
  };
  departures_results_loaded: {
    status: WidgetAnalyticsAsyncStatus;
    selectedLocationId: string | null;
    selectedLocationType: WidgetAnalyticsLocationType;
    stopCount: number;
    errorMessage?: string;
  };
  departures_stop_selected: {
    atcoCode: string;
  };
  departures_board_loaded: {
    status: WidgetAnalyticsAsyncStatus;
    atcoCode: string;
    departureCount: number;
    lineIds: string[];
    operatorCodes: string[];
    operatorNames: string[];
    errorMessage?: string;
  };
  timetables_search_submitted: {
    query: string;
  };
  timetables_results_loaded: {
    status: WidgetAnalyticsAsyncStatus;
    resultCount: number;
    lineIds: string[];
    operatorCodes: string[];
    operatorNames: string[];
    errorMessage?: string;
  };
  timetables_line_selected: {
    lineId: string;
    lineName: string;
    operatorCode: string | null;
    operatorName: string | null;
  };
  timetable_loaded: {
    status: WidgetAnalyticsAsyncStatus;
    lineId: string;
    lineName: string;
    operatorCode: string | null;
    operatorName: string | null;
    direction: string | null;
    date: string | null;
    journeyCount: number;
    errorMessage?: string;
  };
  timetable_controls_submitted: {
    lineId: string;
    operatorCode: string | null;
    direction: string | null;
    date: string | null;
    stopDisplay: string;
  };
  timetable_details_tab_changed: {
    lineId: string;
    tabId: 'timetable' | 'map';
  };
  plan_search_invalid: Record<string, never>;
  departures_search_invalid: Record<string, never>;
  timetables_search_invalid: Record<string, never>;
  departures_board_refreshed: Record<string, never>;
  departures_auto_refresh_changed: {
    enabled: boolean;
  };
}

export type WidgetAnalyticsEventName = keyof WidgetAnalyticsPayloadMap;

export interface WidgetAnalyticsEvent<
  TName extends WidgetAnalyticsEventName = WidgetAnalyticsEventName,
> {
  name: TName;
  activity: WidgetAnalyticsActivity;
  page: WidgetAnalyticsPage;
  payload: WidgetAnalyticsPayloadMap[TName];
}

export interface WidgetAnalyticsEnvelope<
  TName extends WidgetAnalyticsEventName = WidgetAnalyticsEventName,
> {
  schemaVersion: typeof WIDGET_ANALYTICS_SCHEMA_VERSION;
  name: TName;
  emittedAt: string;
  tenant: string;
  activity: WidgetAnalyticsActivity;
  page: WidgetAnalyticsPage;
  widgetVersion: string;
  payload: WidgetAnalyticsPayloadMap[TName];
}
